import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCcw } from 'lucide-react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center">
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-full mb-6">
            <AlertTriangle className="text-red-500" size={48} />
          </div>
          <h1 className="text-3xl font-bold mb-4">Something went wrong</h1>
          <p className="text-zinc-400 max-w-md mb-8">
            The application encountered an unexpected error.
          </p>
          
          <div className="w-full max-w-2xl bg-zinc-900 rounded-xl border border-white/10 p-6 text-left overflow-auto max-h-64 mb-8 custom-scrollbar">
             <p className="text-red-400 font-mono text-sm font-semibold mb-2">
                {this.state.error?.toString()}
             </p>
             {this.state.errorInfo && (
               <pre className="text-zinc-500 font-mono text-xs whitespace-pre-wrap">
                  {this.state.errorInfo.componentStack}
               </pre>
             )}
          </div>

          <button 
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-6 py-3 bg-white text-black rounded-full font-medium hover:bg-zinc-200 transition-colors"
          >
            <RefreshCcw size={18} />
            Reload Application
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;